import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import React, { useEffect, useMemo, useState } from 'react';
import { LayoutChangeEvent, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import Animated, {
  useAnimatedStyle,
  useReducedMotion,
  useSharedValue,
  withRepeat,
  withSequence,
  withSpring,
  withTiming,
} from 'react-native-reanimated';
import Svg, { Circle, G, Line, Polygon, Rect } from 'react-native-svg';

import { AppText } from '@/components/ui/AppText';
import { Card } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { useThemePalette, type Palette } from '@/config/theme';
import type {
  BallSituation,
  DangerLevel,
  LiveAction,
  LiveBall,
  LiveBallState,
  LiveBallStats,
  LiveGoalEvent,
  PossessionSide,
} from '@/features/live/useLiveBallTracker';
import { localizeDigits } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';

// Broadcast-ratio horizontal pitch, matching AveragePositions / the shotmap so
// the live surface reads identically to the rest of the match center.
const PITCH_HEIGHT = 230;
const SAFE_INSET = 10;
// The football render box. The white ball fills it; the soft drop shadow sits a
// touch below. Sized so it reads instantly as a REAL football on the green pitch
// (user tested live and asked for it clearer + bigger), without becoming a marker
// blob.
const BALL_SIZE = 22;
// Trail dots are smaller than the ball and fade fast so the motion stays calm,
// never a strobe of equal-sized discs.
const TRAIL_SIZE = 7;

function twoDecimals(value: number): string {
  return (Math.round(value * 100) / 100).toFixed(2);
}

// ---------------------------------------------------------------------------
// Danger palette — one place that turns a low/medium/high temperature into the
// concrete colours used by the overlay headline, the position bar and the
// territory tint, so they always agree. low = calm cyan, medium = amber/gold,
// high = red. Reads from the active (light/dark) palette.
// ---------------------------------------------------------------------------

interface DangerColors {
  /** Solid accent colour for the headline label + bar core. */
  color: string;
  /** Translucent fill for the position bar / territory shade. */
  soft: string;
  /** A slightly stronger translucent fill for the bar glow. */
  glow: string;
}

function dangerColors(palette: Palette, level: DangerLevel): DangerColors {
  switch (level) {
    case 'high':
      // Red danger moment (rose-red semantic).
      return { color: palette.loss, soft: 'rgba(251,113,133,0.16)', glow: 'rgba(251,113,133,0.30)' };
    case 'medium':
      // Amber building attack (captain gold = the warm/amber token).
      return { color: palette.captain, soft: 'rgba(244,200,75,0.15)', glow: 'rgba(244,200,75,0.28)' };
    case 'low':
    default:
      // Calm open play (cyan accent).
      return { color: palette.voltBright, soft: 'rgba(34,211,255,0.12)', glow: 'rgba(34,211,255,0.22)' };
  }
}

// ---------------------------------------------------------------------------
// Pitch markings (one SVG node). Geometry mirrors AveragePositions' FullPitch.
// ---------------------------------------------------------------------------

const PitchMarkings = React.memo(function PitchMarkings({
  width,
  height,
}: {
  width: number;
  height: number;
}) {
  const palette = useThemePalette();
  const line = palette.pitchLine;
  const cx = width / 2;
  const cy = height / 2;
  const m = SAFE_INSET;
  const innerW = width - m * 2;
  const innerH = height - m * 2;
  const boxH = Math.min(innerH * 0.62, 156);
  const boxW = Math.min(innerW * 0.17, 66);
  const goalAreaH = boxH * 0.52;
  const goalAreaW = boxW * 0.42;
  const goalDepth = Math.max(5, boxW * 0.14);
  const goalH = goalAreaH * 0.62;
  const boxTop = cy - boxH / 2;
  const goalAreaTop = cy - goalAreaH / 2;
  const goalTop = cy - goalH / 2;
  const penFromGoal = boxW * 0.66;
  const r = 1.6;

  return (
    <Svg width={width} height={height} style={{ position: 'absolute', top: 0, left: 0 }} pointerEvents="none">
      <Rect x={m} y={m} width={innerW} height={innerH} rx={6} fill="none" stroke={line} strokeWidth={2} opacity={0.6} />
      <Line x1={cx} y1={m} x2={cx} y2={m + innerH} stroke={line} strokeWidth={2} opacity={0.6} />
      <Circle cx={cx} cy={cy} r={Math.min(innerH * 0.18, 48)} fill="none" stroke={line} strokeWidth={2} opacity={0.5} />
      <Circle cx={cx} cy={cy} r={r} fill={line} opacity={0.7} />

      {/* LEFT end */}
      <Rect x={m} y={boxTop} width={boxW} height={boxH} fill="none" stroke={line} strokeWidth={2} opacity={0.55} />
      <Rect x={m} y={goalAreaTop} width={goalAreaW} height={goalAreaH} fill="none" stroke={line} strokeWidth={2} opacity={0.45} />
      <Rect x={m - goalDepth} y={goalTop} width={goalDepth} height={goalH} fill="none" stroke={line} strokeWidth={2} opacity={0.7} />
      <Circle cx={m + penFromGoal} cy={cy} r={r} fill={line} opacity={0.7} />

      {/* RIGHT end */}
      <Rect x={m + innerW - boxW} y={boxTop} width={boxW} height={boxH} fill="none" stroke={line} strokeWidth={2} opacity={0.55} />
      <Rect x={m + innerW - goalAreaW} y={goalAreaTop} width={goalAreaW} height={goalAreaH} fill="none" stroke={line} strokeWidth={2} opacity={0.45} />
      <Rect x={m + innerW} y={goalTop} width={goalDepth} height={goalH} fill="none" stroke={line} strokeWidth={2} opacity={0.7} />
      <Circle cx={m + innerW - penFromGoal} cy={cy} r={r} fill={line} opacity={0.7} />
    </Svg>
  );
});

// ---------------------------------------------------------------------------
// Coordinate mapping → pixels. x:0..100 LEFT→RIGHT, y:0..100 BOTTOM→TOP (invert).
// ---------------------------------------------------------------------------

function mapX(x: number, width: number): number {
  const usableW = width - SAFE_INSET * 2;
  return SAFE_INSET + (Math.min(Math.max(x, 0), 100) / 100) * usableW;
}
function mapY(y: number, height: number): number {
  const usableH = height - SAFE_INSET * 2;
  return SAFE_INSET + (1 - Math.min(Math.max(y, 0), 100) / 100) * usableH;
}

// ---------------------------------------------------------------------------
// Territory tint — a soft 365-style shade over the half the ball is in, tinted
// by the danger level. Eases between the two halves as the ball crosses centre.
// Subtle (low opacity) so the grass + markings still read through it.
// ---------------------------------------------------------------------------

function TerritoryTint({
  ball,
  width,
  reduced,
  danger,
}: {
  ball: LiveBall;
  width: number;
  reduced: boolean;
  danger: DangerColors;
}) {
  // 0 → ball in the LEFT half, 1 → RIGHT half. Eased so the shade glides across.
  const shift = useSharedValue(ball.x >= 50 ? 1 : 0);

  useEffect(() => {
    const target = ball.x >= 50 ? 1 : 0;
    if (reduced) shift.value = target;
    else shift.value = withTiming(target, { duration: 480 });
  }, [ball.x, reduced, shift]);

  const leftStyle = useAnimatedStyle(() => ({ opacity: 1 - shift.value }));
  const rightStyle = useAnimatedStyle(() => ({ opacity: shift.value }));

  const half = (width - SAFE_INSET * 2) / 2;
  return (
    <>
      <Animated.View
        pointerEvents="none"
        style={[
          { position: 'absolute', top: SAFE_INSET, bottom: SAFE_INSET, left: SAFE_INSET, width: half },
          leftStyle,
        ]}
      >
        <LinearGradient
          colors={[danger.soft, 'transparent']}
          start={{ x: 0, y: 0.5 }}
          end={{ x: 1, y: 0.5 }}
          style={{ flex: 1 }}
        />
      </Animated.View>
      <Animated.View
        pointerEvents="none"
        style={[
          { position: 'absolute', top: SAFE_INSET, bottom: SAFE_INSET, right: SAFE_INSET, width: half },
          rightStyle,
        ]}
      >
        <LinearGradient
          colors={['transparent', danger.soft]}
          start={{ x: 0, y: 0.5 }}
          end={{ x: 1, y: 0.5 }}
          style={{ flex: 1 }}
        />
      </Animated.View>
    </>
  );
}

// ---------------------------------------------------------------------------
// Possession cue — the ball ITSELF is always a white football (so it never loses
// the real-ball look); the team IN POSSESSION is shown by a coloured RING + halo
// AROUND it and by the trail, reusing the SAME two team colours the possession
// bar uses (home = volt/cyan, away = teamAway/coral). Unknown side (basic feed) →
// a neutral white ring, so it still reads as a plain football. Returns the solid
// ring colour + two translucent halo tints (a tight inner glow + a wider outer
// fade) so the cue lifts the ball off the grass without a loud neon blob.
// ---------------------------------------------------------------------------

interface BallColors {
  /** The solid ring colour drawn around the white ball (or white when unknown). */
  ring: string;
  /** A tight inner glow in the same hue, hugging the ball. */
  glow: string;
  /** A wider, softer outer halo in the same hue. */
  halo: string;
  /** The trail colour — the team hue (white when unknown). */
  trail: string;
  /** Whether a team colour is in play (vs the neutral white default). */
  tinted: boolean;
}

function ballColors(palette: Palette, side: PossessionSide): BallColors {
  if (side === 'home')
    return {
      ring: palette.volt,
      glow: 'rgba(34,211,255,0.55)',
      halo: 'rgba(34,211,255,0.28)',
      trail: palette.volt,
      tinted: true,
    };
  if (side === 'away')
    return {
      ring: palette.teamAway,
      glow: 'rgba(251,113,133,0.55)',
      halo: 'rgba(251,113,133,0.28)',
      trail: palette.teamAway,
      tinted: true,
    };
  // No possession side (basic feed) → a plain white football, neutral halo.
  return {
    ring: 'rgba(255,255,255,0.85)',
    glow: 'rgba(255,255,255,0.45)',
    halo: 'rgba(255,255,255,0.22)',
    trail: '#FFFFFF',
    tinted: false,
  };
}

// ---------------------------------------------------------------------------
// The football — a clean WHITE SVG ball that unmistakably reads as a REAL ball on
// the green pitch: a white sphere with classic black pentagon panels + connecting
// seams, a soft lower-right shade for roundness and a small specular highlight
// top-left. The ball is ALWAYS white (never tinted) so the football look is never
// lost to a solid colour; the team IN POSSESSION is shown by the coloured ring
// drawn just inside the rim (cyan home / coral away / white when unknown) plus the
// halo + trail outside it. Drawn once in a fixed 24×24 viewBox so it scales
// crisply at any BALL_SIZE.
// ---------------------------------------------------------------------------

// Classic black football panels (a centre pentagon + partial rim pentagons) and
// the seams that connect them. Authored once at module scope so the polygon
// strings aren't re-allocated on every render.
const BALL_INK = '#15172A';

const Football = React.memo(function Football({
  size,
  ring,
}: {
  size: number;
  /** The possessing-team ring colour, drawn just inside the white rim. */
  ring: string;
}) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" pointerEvents="none">
      {/* White sphere body with a faint grey rim so it reads on a light pitch. */}
      <Circle cx={12} cy={12} r={11} fill="#FFFFFF" stroke="rgba(17,20,34,0.18)" strokeWidth={0.6} />
      {/* Team-colour ring just inside the rim — the possession cue, kept thin so
          the ball still reads white. */}
      <Circle cx={12} cy={12} r={9.7} fill="none" stroke={ring} strokeWidth={1.6} opacity={0.95} />
      {/* Soft lower-right shading so it reads as a sphere, not a flat disc. */}
      <Circle cx={14.8} cy={15.2} r={7.4} fill="rgba(17,20,34,0.08)" />
      <G fill={BALL_INK}>
        {/* Centre pentagon — the unmistakable football mark. */}
        <Polygon points="12,6.9 15.4,9.4 14.1,13.4 9.9,13.4 8.6,9.4" />
        {/* Three partial pentagons around the rim for the panelled look. */}
        <Polygon points="12,2.6 14.1,4.2 13.3,6.5 10.7,6.5 9.9,4.2" opacity={0.92} />
        <Polygon points="3.4,10.8 6,11.3 6.4,13.9 4.2,15.2 2.4,13.2" opacity={0.92} />
        <Polygon points="20.6,10.8 21.6,13.2 19.8,15.2 17.6,13.9 18,11.3" opacity={0.92} />
      </G>
      {/* Seams: thin dark lines from the centre pentagon out to the rim panels. */}
      <G stroke="rgba(17,20,34,0.55)" strokeWidth={0.7}>
        <Line x1={12} y1={6.9} x2={12} y2={4.4} />
        <Line x1={8.6} y1={9.4} x2={6} y2={11.3} />
        <Line x1={15.4} y1={9.4} x2={18} y2={11.3} />
        <Line x1={9.9} y1={13.4} x2={8.4} y2={15.6} />
        <Line x1={14.1} y1={13.4} x2={15.6} y2={15.6} />
      </G>
      {/* Tiny specular highlight, top-left. */}
      <Circle cx={8.8} cy={8.4} r={1.9} fill="rgba(255,255,255,0.95)" />
    </Svg>
  );
});

// ---------------------------------------------------------------------------
// The animated ball: the football, springing smoothly to each new mapped
// position with a soft drop shadow under it (no neon glow). The spring is tuned
// calm — more damping + a touch more mass — so the ball glides, never flashes or
// overshoots into a jitter.
// ---------------------------------------------------------------------------

function AnimatedBall({
  x,
  y,
  width,
  reduced,
  colors,
}: {
  x: number;
  y: number;
  width: number;
  reduced: boolean;
  colors: BallColors;
}) {
  const left = useSharedValue(mapX(x, width));
  const top = useSharedValue(mapY(y, PITCH_HEIGHT));

  useEffect(() => {
    const targetX = mapX(x, width);
    const targetY = mapY(y, PITCH_HEIGHT);
    if (reduced) {
      left.value = targetX;
      top.value = targetY;
    } else {
      // Calm, well-damped spring: glides to the new spot in ~one smooth motion
      // with no visible overshoot/jitter. Higher damping + a touch more mass than
      // before so it reads "رايق وسلس", not snappy.
      const spring = { damping: 26, stiffness: 90, mass: 0.9 };
      left.value = withSpring(targetX, spring);
      top.value = withSpring(targetY, spring);
    }
  }, [x, y, width, reduced, left, top]);

  const ballStyle = useAnimatedStyle(() => ({
    transform: [
      { translateX: left.value - BALL_SIZE / 2 },
      { translateY: top.value - BALL_SIZE / 2 },
    ],
  }));

  return (
    <Animated.View
      style={[
        {
          position: 'absolute',
          top: 0,
          left: 0,
          width: BALL_SIZE,
          height: BALL_SIZE,
          // Soft contact shadow so the ball sits ON the grass instead of glowing
          // over it. Cheap, calm, and reads on both light + dark pitches.
          shadowColor: '#000000',
          shadowOffset: { width: 0, height: 1.5 },
          shadowOpacity: 0.45,
          shadowRadius: 3,
          elevation: 4,
        },
        ballStyle,
      ]}
      pointerEvents="none"
    >
      {/* Wide, soft outer halo in the possessing team's hue (neutral white when
          unknown) — the gentle "who has it" cue that lifts the white ball off the
          grass without tinting the ball itself. */}
      <View
        pointerEvents="none"
        style={{
          position: 'absolute',
          top: -BALL_SIZE * 0.42,
          left: -BALL_SIZE * 0.42,
          width: BALL_SIZE * 1.84,
          height: BALL_SIZE * 1.84,
          borderRadius: BALL_SIZE,
          backgroundColor: colors.halo,
        }}
      />
      {/* Tighter inner glow hugging the ball for a crisper team-colour read. */}
      <View
        pointerEvents="none"
        style={{
          position: 'absolute',
          top: -BALL_SIZE * 0.18,
          left: -BALL_SIZE * 0.18,
          width: BALL_SIZE * 1.36,
          height: BALL_SIZE * 1.36,
          borderRadius: BALL_SIZE,
          backgroundColor: colors.glow,
        }}
      />
      <Football size={BALL_SIZE} ring={colors.ring} />
    </Animated.View>
  );
}

/**
 * A single fading trail node — a small soft dot in the possessing team's hue
 * (the other team-colour cue alongside the ball's ring/halo; neutral white on a
 * basic feed) that shrinks + fades with age so the path reads as a calm wisp
 * behind the ball, never a strobe of equal discs.
 */
const TrailDot = React.memo(function TrailDot({
  x,
  y,
  width,
  opacity,
  size,
  color,
}: {
  x: number;
  y: number;
  width: number;
  opacity: number;
  size: number;
  color: string;
}) {
  return (
    <View
      pointerEvents="none"
      style={{
        position: 'absolute',
        left: mapX(x, width) - size / 2,
        top: mapY(y, PITCH_HEIGHT) - size / 2,
        width: size,
        height: size,
        borderRadius: size / 2,
        backgroundColor: color,
        opacity,
      }}
    />
  );
});

// ---------------------------------------------------------------------------
// Situation status row (BELOW the pitch) — names the CURRENT situation then the
// team in possession ("Dangerous attack — Germany"), with a small icon + coloured
// dot in the team's colour. SET PIECES (corner / throw-in / free kick / goal kick
// / penalty / kick-off) — the moments worth calling out — render as a PROMINENT
// chip tinted in the team colour instead of the quiet inline line, so they pop.
// Replaces the old centred on-pitch overlay so the grass stays clean.
// ---------------------------------------------------------------------------

function SituationRowBase({
  teamColor,
  tinted,
  teamName,
  stateLabel,
  icon,
  iconColor,
  setPiece,
}: {
  /** The possessing team's colour (or a neutral soft tone on a basic feed). */
  teamColor: string;
  /** Whether `teamColor` is a real team colour (vs the neutral default). */
  tinted: boolean;
  teamName: string | null;
  stateLabel: string;
  icon: keyof typeof Ionicons.glyphMap;
  /** Resolved icon colour for the flowing-play line (palette-aware). */
  iconColor: string;
  /** Render the prominent set-piece chip instead of the quiet inline line. */
  setPiece: boolean;
}) {
  // SET PIECE → a prominent pill: tinted fill + team-coloured border, icon, the
  // situation in the team colour, then " — Team". The cue the user asked to be
  // unmistakable for restarts.
  if (setPiece) {
    return (
      <View
        className="flex-row items-center gap-1.5 self-start rounded-full px-2.5 py-1"
        style={{
          backgroundColor: tinted ? `${teamColor}22` : 'rgba(198,201,230,0.14)',
          borderWidth: 1,
          borderColor: tinted ? `${teamColor}66` : 'rgba(198,201,230,0.30)',
        }}
      >
        <Ionicons name={icon} size={13} color={teamColor} />
        <AppText variant="caption" weight="bold" style={{ color: teamColor }} numberOfLines={1}>
          {stateLabel}
        </AppText>
        {teamName ? (
          <AppText variant="caption" weight="semibold" className="text-soft" numberOfLines={1}>
            {`· ${teamName}`}
          </AppText>
        ) : null}
      </View>
    );
  }

  // FLOWING PLAY → a refined inline line: a small coloured dot + icon, the
  // situation (bold, ink) then "— Team" (the team in the soft tone).
  return (
    <View className="flex-row items-center gap-2">
      <View className="flex-row items-center gap-1.5">
        <View
          style={{
            width: 8,
            height: 8,
            borderRadius: 4,
            backgroundColor: teamColor,
            // A hairline lift so a coloured dot still reads on either theme.
            shadowColor: teamColor,
            shadowOffset: { width: 0, height: 0 },
            shadowOpacity: 0.6,
            shadowRadius: 3,
            elevation: 2,
          }}
        />
        <Ionicons name={icon} size={13} color={tinted ? teamColor : iconColor} />
      </View>
      <AppText variant="caption" weight="bold" className="text-ink" numberOfLines={1}>
        {stateLabel}
        {teamName ? (
          <AppText variant="caption" weight="semibold" className="text-soft">
            {`  ·  ${teamName}`}
          </AppText>
        ) : null}
      </AppText>
    </View>
  );
}

// Re-render only when the possessing colour, team name, situation label, icon or
// set-piece flag change — NOT on the ~5s ball-position tick.
const SituationRow = React.memo(
  SituationRowBase,
  (prev, next) =>
    prev.teamColor === next.teamColor &&
    prev.tinted === next.tinted &&
    prev.teamName === next.teamName &&
    prev.stateLabel === next.stateLabel &&
    prev.icon === next.icon &&
    prev.iconColor === next.iconColor &&
    prev.setPiece === next.setPiece,
);

// ---------------------------------------------------------------------------
// The action pulse: a brief ring at the action's mapped coords (goal/shot/card).
// ---------------------------------------------------------------------------

function ActionPulse({
  action,
  width,
  reduced,
  color,
}: {
  action: LiveAction;
  width: number;
  reduced: boolean;
  color: string;
}) {
  const scale = useSharedValue(0.2);
  const opacity = useSharedValue(0);

  useEffect(() => {
    if (reduced) return;
    scale.value = 0.2;
    opacity.value = 0.7;
    scale.value = withTiming(2.4, { duration: 900 });
    opacity.value = withSequence(withTiming(0.7, { duration: 80 }), withTiming(0, { duration: 820 }));
  }, [action.id, reduced, scale, opacity]);

  const style = useAnimatedStyle(() => ({
    opacity: opacity.value,
    transform: [{ scale: scale.value }],
  }));

  if (action.x === null || action.y === null) return null;
  const size = 30;
  return (
    <Animated.View
      pointerEvents="none"
      style={[
        {
          position: 'absolute',
          left: mapX(action.x, width) - size / 2,
          top: mapY(action.y, PITCH_HEIGHT) - size / 2,
          width: size,
          height: size,
          borderRadius: size / 2,
          borderWidth: 2,
          borderColor: color,
        },
        style,
      ]}
    />
  );
}

// ---------------------------------------------------------------------------
// Goal / red-card celebratory flash — a brief, tasteful full-pitch banner that
// pops on a real goal or red card, then self-dismisses after ~2s. Driven by the
// hook's `goalEvent` (a monotonic id, so two goals at the same scoreline still
// re-trigger). Reanimated: scales + fades in, holds, then fades out. Honours
// reduced-motion (no scale, instant) and never blocks touches (pointerEvents off).
// ---------------------------------------------------------------------------

const FLASH_HOLD_MS = 2000;

function GoalFlashBase({ event }: { event: LiveGoalEvent }) {
  const palette = useThemePalette();
  const { t } = useTranslation();
  const reduced = useReducedMotion();

  const opacity = useSharedValue(0);
  const scale = useSharedValue(0.7);

  // Re-arm + auto-dismiss whenever a NEW event (id) arrives. The flash fades fully
  // out to opacity 0 by ~FLASH_HOLD_MS, so the node self-dismisses purely via the
  // animation (no extra state to toggle). It stays mounted but invisible and
  // non-interactive (pointerEvents off) until the next event re-runs this.
  useEffect(() => {
    if (reduced) {
      // No scale; a gentle fade in→hold→out so it still self-dismisses calmly.
      scale.value = 1;
      opacity.value = withSequence(
        withTiming(1, { duration: 1 }),
        withTiming(1, { duration: FLASH_HOLD_MS - 300 }),
        withTiming(0, { duration: 299 }),
      );
      return;
    }
    opacity.value = withSequence(
      withTiming(1, { duration: 220 }),
      withTiming(1, { duration: FLASH_HOLD_MS - 520 }),
      withTiming(0, { duration: 300 }),
    );
    scale.value = withSequence(
      withSpring(1.06, { damping: 9, stiffness: 140 }),
      withTiming(1, { duration: 160 }),
    );
  }, [event.id, reduced, opacity, scale]);

  const style = useAnimatedStyle(() => ({
    opacity: opacity.value,
    transform: [{ scale: scale.value }],
  }));

  const isRed = event.kind === 'red_card';
  const accent = isRed ? palette.loss : palette.volt;
  const headline = isRed ? t('liveAlerts.flashRedCard') : t('liveAlerts.flashGoal');

  return (
    <Animated.View
      pointerEvents="none"
      className="absolute inset-0 items-center justify-center"
      style={style}
    >
      <View
        className="items-center rounded-3xl px-7 py-4"
        style={{
          backgroundColor: 'rgba(5,5,14,0.62)',
          borderWidth: 1.5,
          borderColor: accent,
          shadowColor: accent,
          shadowOffset: { width: 0, height: 0 },
          shadowOpacity: 0.8,
          shadowRadius: 18,
          elevation: 8,
        }}
      >
        <Ionicons name={isRed ? 'square' : 'football'} size={30} color={accent} />
        <AppText
          variant="title"
          weight="bold"
          className="mt-1 uppercase tracking-widest"
          style={{ color: accent }}
          numberOfLines={1}
        >
          {headline}
        </AppText>
        {event.player ? (
          <AppText
            variant="caption"
            weight="bold"
            style={{ color: '#FFFFFF' }}
            numberOfLines={1}
          >
            {event.player}
          </AppText>
        ) : null}
      </View>
    </Animated.View>
  );
}

// Re-render only when a genuinely new flash event arrives (id changes).
const GoalFlash = React.memo(GoalFlashBase, (prev, next) => prev.event.id === next.event.id);

// ---------------------------------------------------------------------------
// Situation label resolution (shared by the overlay + the badge).
// ---------------------------------------------------------------------------

function situationLabel(t: (k: string) => string, situation: BallSituation): string {
  const key = `livePitch.situations.${situation}`;
  const label = t(key);
  // i18next returns the key itself when missing → fall back to open play.
  return label === key ? t('livePitch.situations.regular_play') : label;
}

// Restart/dead-ball moments the user wants called out clearly — these render as a
// PROMINENT chip (icon + label) instead of the quiet inline status line. Covers
// both the documented tags and the real live values that mean the same thing.
const SET_PIECES: ReadonlySet<string> = new Set([
  'free_kick',
  'corner',
  'corner_kick',
  'goal_kick',
  'throw_in',
  'penalty',
  'kick_off',
]);

function isSetPiece(situation: BallSituation): boolean {
  return SET_PIECES.has(situation);
}

// A small glyph per situation so the status line / chip reads at a glance. Set
// pieces get a literal icon (corner flag, throw-in hands, penalty spot…); the
// flowing states get a soft motion/temperature cue. Anything unmapped → a neutral
// football, so a new tag still renders.
const SITUATION_ICON: Record<string, keyof typeof Ionicons.glyphMap> = {
  // set pieces
  free_kick: 'football-outline',
  corner: 'flag',
  corner_kick: 'flag',
  goal_kick: 'football-outline',
  throw_in: 'hand-left-outline',
  penalty: 'alert-circle',
  kick_off: 'play-circle-outline',
  // flowing play
  regular_play: 'ellipse-outline',
  safe: 'shield-checkmark-outline',
  attack: 'trending-up-outline',
  dangerous_attack: 'flame',
  shotoffwoodwork: 'ellipse-outline',
  goal: 'football',
};

function situationIcon(situation: BallSituation): keyof typeof Ionicons.glyphMap {
  return SITUATION_ICON[situation] ?? 'football';
}

// ---------------------------------------------------------------------------
// Pulsing LIVE dot
// ---------------------------------------------------------------------------

function PulsingDot() {
  const reduced = useReducedMotion();
  const pulse = useSharedValue(1);
  useEffect(() => {
    if (!reduced) pulse.value = withRepeat(withTiming(0.3, { duration: 900 }), -1, true);
  }, [pulse, reduced]);
  const style = useAnimatedStyle(() => ({ opacity: pulse.value }));
  return (
    <Animated.View style={style}>
      <View className="h-2 w-2 rounded-full bg-live" />
    </Animated.View>
  );
}

// ---------------------------------------------------------------------------
// Action ticker line (goal / yellow / red / shot / save)
// ---------------------------------------------------------------------------

const ACTION_LABEL_KEY: Record<string, string> = {
  goal: 'livePitch.actions.goal',
  shot: 'livePitch.actions.shot',
  yellow_card: 'livePitch.actions.yellow',
  red_card: 'livePitch.actions.red',
  save: 'livePitch.actions.save',
  corner: 'livePitch.actions.corner',
  corner_kick: 'livePitch.actions.corner',
  penalty: 'livePitch.actions.penalty',
  post: 'livePitch.actions.woodwork',
  shotoffwoodwork: 'livePitch.actions.woodwork',
};

const ACTION_ICON: Record<string, keyof typeof Ionicons.glyphMap> = {
  goal: 'football',
  shot: 'flame-outline',
  yellow_card: 'square',
  red_card: 'square',
  save: 'hand-left-outline',
  corner: 'flag-outline',
  corner_kick: 'flag-outline',
  penalty: 'alert-circle-outline',
  post: 'ellipse-outline',
  shotoffwoodwork: 'ellipse-outline',
};

function tickerColor(palette: Palette, action: LiveAction): string {
  if (action.action_type === 'yellow_card') return palette.captain;
  if (action.action_type === 'red_card' || action.action_type === 'penalty') return palette.loss;
  if (action.action_type === 'goal') return palette.volt;
  return palette.soft;
}

function ActionTickerBase({ action }: { action: LiveAction }) {
  const palette = useThemePalette();
  const { t } = useTranslation();
  const { arabicNumerals } = useLocale();
  const reduced = useReducedMotion();
  const enter = useSharedValue(0);

  useEffect(() => {
    enter.value = reduced ? 1 : 0;
    if (!reduced) enter.value = withTiming(1, { duration: 280 });
  }, [action.id, reduced, enter]);

  const style = useAnimatedStyle(() => ({
    opacity: enter.value,
    transform: [{ translateY: (1 - enter.value) * 6 }],
  }));

  const labelKey = ACTION_LABEL_KEY[action.action_type];
  if (!labelKey) return null;
  const icon = ACTION_ICON[action.action_type] ?? 'ellipse-outline';
  const color = tickerColor(palette, action);
  const minute =
    action.minute !== null ? `${localizeDigits(String(action.minute), arabicNumerals)}'` : null;

  return (
    <Animated.View
      style={[style, { borderColor: `${color}3D`, backgroundColor: `${color}14` }]}
      className="mx-4 mb-3 flex-row items-center gap-2.5 overflow-hidden rounded-2xl border px-3 py-2"
    >
      {/* A left accent rail + icon bubble in the action's colour so the latest
          event reads as a deliberate ticker, not a loose row. */}
      <View style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, backgroundColor: color }} />
      <View
        className="items-center justify-center rounded-full"
        style={{ width: 22, height: 22, backgroundColor: `${color}29` }}
      >
        <Ionicons name={icon} size={13} color={color} />
      </View>
      {minute ? (
        <AppText variant="micro" weight="bold" tabular style={{ color }}>
          {minute}
        </AppText>
      ) : null}
      <AppText variant="caption" weight="bold" style={{ color }} numberOfLines={1}>
        {t(labelKey)}
      </AppText>
      {action.player ? (
        <AppText variant="caption" weight="medium" className="flex-1 text-soft" numberOfLines={1}>
          {action.player}
        </AppText>
      ) : null}
    </Animated.View>
  );
}

// Re-render only when a genuinely new action arrives (id changes), never on the
// ~5s livedata tick that re-runs the parent.
const ActionTicker = React.memo(
  ActionTickerBase,
  (prev, next) => prev.action.id === next.action.id,
);

// ---------------------------------------------------------------------------
// Live stat strip: possession bar + shots + xG
// ---------------------------------------------------------------------------

// Narrow prop (just `stats`, not the whole live state) so React.memo skips the
// re-render on every ~5s livedata tick — the stat block only changes on an
// `event`/`subscribed` frame, not when the ball position moves.
const StatStrip = React.memo(function StatStrip({ stats }: { stats: LiveBallStats }) {
  const palette = useThemePalette();
  const { t } = useTranslation();
  const { arabicNumerals } = useLocale();
  const num = (v: number) => localizeDigits(String(Math.round(v)), arabicNumerals);

  const poss = stats.possession;
  const totalPoss = poss.home + poss.away;
  const homeShare = totalPoss > 0 ? poss.home / totalPoss : 0.5;

  return (
    <View className="border-t border-line px-4 pb-3 pt-3" style={{ direction: 'ltr' }}>
      {/* Possession — percentages flanked by a team-colour dot (the legend for
          which end of the bar is which), with a centred section label. */}
      <View className="mb-1.5 flex-row items-center justify-between">
        <View className="flex-row items-center gap-1.5">
          <View style={{ width: 7, height: 7, borderRadius: 4, backgroundColor: palette.volt }} />
          <AppText variant="caption" weight="bold" tabular className="text-ink">
            {`${num(poss.home)}%`}
          </AppText>
        </View>
        <AppText variant="micro" weight="bold" className="uppercase tracking-widest text-muted">
          {t('match.possession')}
        </AppText>
        <View className="flex-row items-center gap-1.5">
          <AppText variant="caption" weight="bold" tabular className="text-ink">
            {`${num(poss.away)}%`}
          </AppText>
          <View style={{ width: 7, height: 7, borderRadius: 4, backgroundColor: palette.teamAway }} />
        </View>
      </View>
      <View className="h-2 flex-row overflow-hidden rounded-full bg-inset">
        <View style={{ flex: Math.max(homeShare, 0.02), backgroundColor: palette.volt }} />
        <View style={{ width: 2, backgroundColor: palette.deep }} />
        <View style={{ flex: Math.max(1 - homeShare, 0.02), backgroundColor: palette.teamAway }} />
      </View>

      {/* Shots + xG — two refined cells split by a hairline divider, each with the
          metric label centred between the home/away figures. */}
      <View className="mt-3 flex-row items-stretch rounded-2xl border border-line bg-inset/50">
        <View className="flex-1 flex-row items-center justify-center gap-2 py-2">
          <AppText variant="caption" weight="bold" tabular className="text-ink">
            {num(stats.shots.home)}
          </AppText>
          <AppText variant="micro" weight="bold" className="uppercase tracking-wider text-muted">
            {t('match.shots')}
          </AppText>
          <AppText variant="caption" weight="bold" tabular className="text-ink">
            {num(stats.shots.away)}
          </AppText>
        </View>
        <View className="my-1.5 w-px bg-line" />
        <View className="flex-1 flex-row items-center justify-center gap-2 py-2">
          <AppText variant="caption" weight="bold" tabular className="text-volt-bright">
            {localizeDigits(twoDecimals(stats.xg.home), arabicNumerals)}
          </AppText>
          <AppText variant="micro" weight="bold" className="uppercase tracking-wider text-muted">
            {t('match.xgShort')}
          </AppText>
          <AppText variant="caption" weight="bold" tabular className="text-volt-bright">
            {localizeDigits(twoDecimals(stats.xg.away), arabicNumerals)}
          </AppText>
        </View>
      </View>
    </View>
  );
});

// ---------------------------------------------------------------------------
// The card
// ---------------------------------------------------------------------------

export interface LiveBallPitchProps {
  state: LiveBallState;
  homeShortName: string;
  awayShortName: string;
}

/**
 * Live Pitch — a glassy Volt card that renders the Bzzoiro live ball-tracking
 * feed as a smooth, calm ("رايق") horizontal pitch in a 365scores idiom. The
 * ball springs between positions and leaves a short fading trail; a centre
 * overlay names the team IN POSSESSION with the danger-state label under it
 * (red dangerous-attack / amber attack / calm open play), a slim vertical bar
 * marks the ball's x-position tinted by that danger level, and a subtle
 * territory tint shades the half under pressure. A live header + stat strip
 * frame it. FULL-feed actions add a brief pulse + a ticker line; short live
 * commentary shows small + secondary.
 *
 * ONE fixed left→right axis (home LEFT, away RIGHT) is shared by the header, the
 * possession strip AND the pitch, so the team attacking the right goal sits on
 * the right in every locale. All three are locked `direction: 'ltr'` for that —
 * the ball's x:0..100 (LEFT→RIGHT) must never mirror — while the team-name and
 * score text inside still render RTL-correctly (bidi isolates lock the score).
 *
 * Connection states render in place: a calm skeleton while connecting, a subtle
 * inline note while reconnecting, and graceful messages for not-tracked / error.
 * The caller mounts this only for LIVE matches that carry a provider_match_id.
 *
 * Perf: the heavy chrome (overlay, stat strip, ticker) is memoized on its own
 * inputs, so a ~5s livedata tick only moves the ball + trail + position bar +
 * territory tint and updates the overlay text when the danger/side changes.
 */
export function LiveBallPitch({ state, homeShortName, awayShortName }: LiveBallPitchProps) {
  const palette = useThemePalette();
  const { t } = useTranslation();
  const { arabicNumerals } = useLocale();
  const reduced = useReducedMotion();
  const [width, setWidth] = useState(0);

  const onLayout = (e: LayoutChangeEvent) => setWidth(e.nativeEvent.layout.width);

  const minuteLabel = useMemo(() => {
    if (state.time.minute === null) return null;
    return `${localizeDigits(String(state.time.minute), arabicNumerals)}'`;
  }, [state.time.minute, arabicNumerals]);

  // Prefer the provider's pre-formatted min:sec clock ("24:14"); fall back to the
  // minute label. Digits localized for Arabic.
  const clockLabel = useMemo(() => {
    if (state.time.display) return localizeDigits(state.time.display, arabicNumerals);
    return minuteLabel;
  }, [state.time.display, minuteLabel, arabicNumerals]);

  // The header row + pitch are pinned LTR (home name always LEFT, away RIGHT) in
  // BOTH locales, so the score must ALWAYS read home–away — never reversed for
  // Arabic, or the digits would sit under the wrong team. LRI(⁦)/PDI(⁩) lock the
  // segment LTR so the bidi algorithm can't re-flip a multi-digit "10 - 2".
  const score = `⁦${localizeDigits(
    `${state.score.home} - ${state.score.away}`,
    arabicNumerals,
  )}⁩`;

  // Trail fades from newest (idx 0, strong) to oldest.
  const trail = state.trail;
  const ball = state.ball;
  const onPitch = state.status === 'live' || state.status === 'reconnecting';
  const danger: DangerLevel = ball ? ball.danger : 'low';
  const dColors = dangerColors(palette, danger);

  // The ball stays a WHITE football; the team IN POSSESSION shows as its ring +
  // halo + trail, reusing the SAME two colours the possession bar uses (home =
  // volt/cyan, away = teamAway/coral). Unknown side → neutral white. The status
  // row below the pitch echoes the team colour + names the current situation.
  const bColors = ballColors(palette, ball?.side ?? null);

  // The team currently in possession. Falls back to null when the feed has no
  // `side` (basic source) — the status row then shows just the situation.
  const possessingName =
    ball?.side === 'home' ? homeShortName : ball?.side === 'away' ? awayShortName : null;
  const situation: BallSituation = ball ? ball.situation : 'regular_play';
  const stateLabel = situationLabel(t, situation);
  const stateIcon = situationIcon(situation);
  const setPiece = isSetPiece(situation);
  // The status row's team colour: the possessing-team hue when known, else the
  // neutral soft tone (so a basic feed reads calm, not falsely "home cyan").
  const situationColor = bColors.tinted ? bColors.ring : palette.soft;

  return (
    <Card elevated className="mx-4 mb-4 overflow-hidden p-0">
      {/* Glossy live sheen */}
      <LinearGradient
        colors={['rgba(34,211,255,0.10)', 'rgba(34,211,255,0)']}
        start={{ x: 0, y: 0 }}
        end={{ x: 0, y: 1 }}
        style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 90 }}
        pointerEvents="none"
      />

      {/* Title + LIVE chip */}
      <View className="flex-row items-center justify-between px-4 pb-1 pt-3">
        <AppText variant="micro" weight="bold" className="uppercase tracking-widest text-soft">
          {t('livePitch.title')}
        </AppText>
        <View
          className="flex-row items-center gap-1.5 rounded-full px-2 py-1"
          style={{ backgroundColor: palette.voltGlass }}
        >
          <PulsingDot />
          <AppText variant="micro" weight="bold" className="uppercase tracking-widest text-live">
            {t('common.live')}
          </AppText>
        </View>
      </View>

      {/* Header: home short · score · away short + minute/period. Locked LTR so
          home stays LEFT in both locales, agreeing with the possession strip and
          the pitch (single shared left→right axis). */}
      <View
        className="flex-row items-center justify-between gap-3 px-4 pb-2.5 pt-1"
        style={{ direction: 'ltr' }}
      >
        <AppText variant="heading" weight="bold" className="flex-1 text-ink" numberOfLines={1}>
          {homeShortName}
        </AppText>
        <View className="items-center">
          <AppText variant="title" weight="bold" tabular className="text-ink">
            {score}
          </AppText>
          {clockLabel || state.time.period ? (
            <AppText variant="micro" weight="bold" tabular className="text-live">
              {[state.time.period, clockLabel].filter(Boolean).join(' · ')}
            </AppText>
          ) : null}
        </View>
        <AppText
          variant="heading"
          weight="bold"
          className="flex-1 text-right text-ink"
          numberOfLines={1}
        >
          {awayShortName}
        </AppText>
      </View>

      {/* The pitch — always LTR (home left, away right). */}
      <View
        className="overflow-hidden bg-pitch"
        style={{ height: PITCH_HEIGHT, direction: 'ltr' }}
        onLayout={onLayout}
      >
        {/* Subtle vignette so the ball reads against the grass. */}
        <LinearGradient
          colors={['rgba(0,0,0,0.18)', 'rgba(0,0,0,0)', 'rgba(0,0,0,0.18)']}
          start={{ x: 0, y: 0 }}
          end={{ x: 0, y: 1 }}
          style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
          pointerEvents="none"
        />

        {/* Territory tint (under the markings) — only when we have a ball. */}
        {width > 0 && onPitch && ball ? (
          <TerritoryTint ball={ball} width={width} reduced={reduced} danger={dColors} />
        ) : null}

        {width > 0 ? <PitchMarkings width={width} height={PITCH_HEIGHT} /> : null}

        {/* Connecting → calm skeleton overlay. */}
        {state.status === 'connecting' ? (
          <View className="absolute inset-0 items-center justify-center px-6">
            <Skeleton className="mb-3 h-3 w-32" />
            <AppText variant="caption" weight="medium" className="text-center text-soft">
              {t('livePitch.connecting')}
            </AppText>
          </View>
        ) : null}

        {/* not_tracked → friendly message, no ball. */}
        {state.status === 'not_tracked' ? (
          <View className="absolute inset-0 items-center justify-center px-6">
            <Ionicons name="navigate-circle-outline" size={26} color={palette.muted} />
            <AppText variant="caption" weight="medium" className="mt-2 text-center text-muted">
              {t('livePitch.notTracked')}
            </AppText>
          </View>
        ) : null}

        {/* error → graceful inline message. */}
        {state.status === 'error' ? (
          <View className="absolute inset-0 items-center justify-center px-6">
            <Ionicons name="cloud-offline-outline" size={26} color={palette.muted} />
            <AppText variant="caption" weight="medium" className="mt-2 text-center text-muted">
              {t('livePitch.error')}
            </AppText>
          </View>
        ) : null}

        {/* Trail + ball + action pulse (when live). The pitch surface stays
            clean — markings + ball only; the team/situation read lives in the
            status row BELOW the green. */}
        {width > 0 && onPitch ? (
          <>
            {trail.map((point, index) => (
              <TrailDot
                key={`${point.x}-${point.y}-${index}`}
                x={point.x}
                y={point.y}
                width={width}
                color={bColors.trail}
                // Calm wisp: newest dot is faint (0.24) and each older one fades +
                // shrinks fast, so the tail is a soft hint of the path, never a
                // bright dotted line.
                opacity={Math.max(0, 0.24 - index * 0.045)}
                size={Math.max(3, TRAIL_SIZE - index)}
              />
            ))}
            {state.lastAction ? (
              <ActionPulse
                action={state.lastAction}
                width={width}
                reduced={reduced}
                color={tickerColor(palette, state.lastAction)}
              />
            ) : null}
            {ball ? (
              <AnimatedBall x={ball.x} y={ball.y} width={width} reduced={reduced} colors={bColors} />
            ) : null}
          </>
        ) : null}

        {/* Celebratory GOAL / RED CARD flash — pops on a real event, self-dismisses
            after ~2s. Sits above every pitch layer; never blocks touches. */}
        {state.goalEvent ? <GoalFlash event={state.goalEvent} /> : null}
      </View>

      {/* Situation status row — names the current situation then the team in
          possession ("Dangerous attack — Germany"); SET PIECES render as a
          prominent team-coloured chip. The clean read that used to float on the
          pitch, now premium + clearly readable under the green. */}
      {onPitch && ball ? (
        <View className="flex-row items-center justify-between gap-2 px-4 pb-0.5 pt-2.5">
          <SituationRow
            teamColor={situationColor}
            tinted={bColors.tinted}
            teamName={possessingName}
            stateLabel={stateLabel}
            icon={stateIcon}
            iconColor={palette.soft}
            setPiece={setPiece}
          />
        </View>
      ) : null}

      {/* Live commentary (short, English as-is) + reconnecting note. The mic glyph
          sits in a small tinted bubble so the commentary reads as its own premium
          element, not loose text under the situation row. */}
      <View className="min-h-[24px] flex-row items-center justify-between gap-2 px-4 pb-1 pt-1.5">
        {ball?.commentary && onPitch ? (
          <View className="flex-1 flex-row items-center gap-2">
            <View
              className="items-center justify-center rounded-full"
              style={{ width: 18, height: 18, backgroundColor: palette.voltGlass }}
            >
              <Ionicons name="mic" size={11} color={palette.volt} />
            </View>
            <AppText variant="micro" weight="medium" className="flex-1 text-soft" numberOfLines={1}>
              {ball.commentary}
            </AppText>
          </View>
        ) : (
          <View className="flex-1" />
        )}
        {state.status === 'reconnecting' ? (
          <View className="flex-row items-center gap-1.5">
            <Ionicons name="sync-outline" size={11} color={palette.muted} />
            <AppText variant="micro" weight="medium" className="text-muted" numberOfLines={1}>
              {t('livePitch.reconnecting')}
            </AppText>
          </View>
        ) : null}
      </View>

      {/* Action ticker (latest goal/shot/card). */}
      {state.lastAction && ACTION_LABEL_KEY[state.lastAction.action_type] ? (
        <ActionTicker action={state.lastAction} />
      ) : (
        <View className="h-1" />
      )}

      {/* Live stat strip. */}
      <StatStrip stats={state.stats} />
    </Card>
  );
}
